import React, { useState, useEffect, useContext } from 'react';
import { Box, VStack, Spinner, Text, Alert, AlertIcon, AlertTitle, AlertDescription, HStack, Select, Button, Tabs, TabList, TabPanels, Tab, TabPanel, useToast, Link } from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { useStrictAnalysisData } from './useStrictAnalysisData';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import CaseHeaderPanel from './CaseHeaderPanel';
import CarrierInfoPanel from './CarrierInfoPanel';
import TechReportMetadataPanel from './TechReportMetadataPanel';
import MedicalNecessityPanel from './MedicalNecessityPanel';
import CodingAnalysisPanel from './CodingAnalysisPanel';
import DocumentManagerPanel from './DocumentManagerPanel';
import HeaderMenus from '../HeaderMenus';
import ModernChat from '../ModernChat';

const USNStrictAnalysis = ({ caseId }) => {
  const toast = useToast();
  const [isRecoding, setIsRecoding] = useState(false);
  const {
    initialLoading,
    error,
    caseData,
    carrierData,
    caseFiles,
    results,
    claimsData,
    claimType,
    setClaimType,
    pipelineVersion,
    skipFetchRef,
    isNMA,
    isAdmin,
    refreshData,
    techMetadata,
    setTechMetadata,
    isTechMetadataLoading
  } = useStrictAnalysisData(caseId);

  const { accessToken } = useContext(AppContext);
  const [loadingMode, setLoadingMode] = useState(null);
  const [isUsnPdfGenerating, setIsUsnPdfGenerating] = useState(false);
  const [localError, setLocalError] = useState(null);

  const [hasV2Data, setHasV2Data] = useState(false);

  useEffect(() => {
    const checkV3Data = async () => {
      if (!caseId || !accessToken) return;
      try {
        const response = await callApi(`/usn/v3/analyze_case/${caseId}?claim_type=${claimType}`, accessToken, { method: 'GET' });
        if (response.ok) {
          const data = await response.json();
          setHasV2Data(data.status === 'success' && !!data.results);
        } else {
          setHasV2Data(false);
        }
      } catch (err) {
        console.error("Error checking V3 data:", err);
        setHasV2Data(false);
      }
    };
    checkV3Data();
  }, [caseId, claimType, accessToken]);


  const runUSNAnalysis = (forceRefresh) => {
    setLoadingMode(forceRefresh ? 'usn_refresh' : 'usn');
    skipFetchRef.current = true;
    // Actual API call logic would go here
  };

  const handleGenerateUsnPdf = async () => {
    if (!caseId || !claimType) {
      setLocalError('Case ID or Claim Type is missing.');
      return;
    }

    setIsUsnPdfGenerating(true);
    setLocalError(null);
    try {
      const response = await callApi(`/v2/cases/${caseId}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'usneuro_ionm_cms1500_v1',
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          setLocalError('Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate USN PDF.');
      }
    } catch (err) {
      console.error('Error generating USN PDF:', err);
      setLocalError(err.message);
    } finally {
      setIsUsnPdfGenerating(false);
    }
  };

  if (initialLoading && !results) {
    return (
      <Box>
        <HeaderMenus />
        <VStack py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>Checking for existing analysis...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1200px" margin="0 auto">
        
        <CaseHeaderPanel 
          caseData={caseData} 
          pipelineVersion={pipelineVersion} 
          isNMA={isNMA} 
          isAdmin={isAdmin} 
        >
          <HStack spacing={4}>
            <Select 
              value={claimType} 
              onChange={(e) => setClaimType(e.target.value)}
              width="150px"
              bg="white"
            >
              <option value="pro">Pro Claim</option>
              <option value="tech">Tech Claim</option>
            </Select>
            <Button 
              colorScheme="teal" 
              onClick={() => runUSNAnalysis(false)} 
              isLoading={loadingMode === 'usn'}
              loadingText="Running USN..."
            >
              Run USN Analysis
            </Button>
            <Button 
              colorScheme="blue" 
              isLoading={isRecoding}
              loadingText="Queueing..."
              onClick={async () => {
                setIsRecoding(true);
                try {
                  const response = await callApi('/api/jobs', accessToken, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      job_type: 'recode',
                      payload: {
                        case_id: caseId,
                        external_case_id: caseId
                      }
                    })
                  });
                  if (response.ok) {
                    toast({
                      title: 'Job Queued',
                      description: 'Recode job has been successfully queued.',
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  } else {
                    throw new Error('Failed to queue job');
                  }
                } catch (error) {
                  toast({
                    title: 'Error',
                    description: 'Failed to queue recode job.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                } finally {
                  setTimeout(() => setIsRecoding(false), 2000);
                }
              }}
            >
              Re-code Case
            </Button>
            <Button
              colorScheme="teal"
              leftIcon={<DownloadIcon />}
              onClick={handleGenerateUsnPdf}
              isLoading={isUsnPdfGenerating}
              loadingText="Generating..."
            >
              Generate USN PDF
            </Button>
          </HStack>
        </CaseHeaderPanel>

        {(error || localError) && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error || localError}</AlertDescription>
            </Box>
          </Alert>
        )}

        
        {hasV2Data && (
          <Box mb={4} textAlign="right">
            <Link 
              href={`/usn-compare-v2/${caseId}`} 
              isExternal 
              color="purple.600" 
              fontWeight="medium"
              fontSize="sm"
              _hover={{ color: "purple.800", textDecoration: "underline" }}
            >
              View V3 Comparison ↗
            </Link>
          </Box>
        )}

        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>Strict Analysis</Tab>
            <Tab>Dx Chat</Tab>
            <Tab>CPT Chat</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} py={4}>
              
              <CarrierInfoPanel data={carrierData} />
              
              <TechReportMetadataPanel metadata={techMetadata} isLoading={isTechMetadataLoading} />
              
              {loadingMode && !results ? (
                <VStack py={12} spacing={4}>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text>Running strict rule evaluation and scoring...</Text>
                </VStack>
              ) : (
                <>
                  <MedicalNecessityPanel results={results} />
                  <CodingAnalysisPanel results={results} claimsData={claimsData} claimType={claimType} />
                </>
              )}
              
              <DocumentManagerPanel caseId={caseId} caseFiles={caseFiles} onUploadSuccess={refreshData} />

            </TabPanel>

            <TabPanel>
              <Box h="600px">
                <ModernChat 
                  caseId={caseId} 
                  contextType="usn_dx_context" 
                  height="600px"
                  additionalContext={results ? JSON.stringify(results, null, 2) : null}
                />
              </Box>
            </TabPanel>

            <TabPanel>
              <Box h="600px">
                <ModernChat 
                  caseId={caseId} 
                  contextType="cpt_ionm_context" 
                  height="600px"
                  additionalContext={results ? JSON.stringify(results, null, 2) : null}
                />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

      </Box>
    </Box>
  );
};

export default USNStrictAnalysis;
